import React from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import Icon from 'components/icon/icon';
import Loader, { LoaderSize } from 'components/loader/loader';

export enum MediaBoxAspect {
    PORTRAIT = 'PORTRAIT',
    SQUARE = 'SQUARE',
    LANDSCAPE = 'LANDSCAPE',
    FREE = 'FREE',
    WIDE_LANDSCAPE = 'WIDE_LANDSCAPE',
    NONE = 'NONE',
    IPHONE6 = 'IPHONE6',
    INTRO_BANNER = 'INTRO_BANNER',
}

const mapAspectToClasses = {
    [MediaBoxAspect.PORTRAIT]: 'aspect--portrait',
    [MediaBoxAspect.SQUARE]: 'aspect--square',
    [MediaBoxAspect.LANDSCAPE]: 'aspect--landscape',
    [MediaBoxAspect.WIDE_LANDSCAPE]: 'aspect--wide-landscape',
    [MediaBoxAspect.FREE]: 'aspect--free',
    [MediaBoxAspect.NONE]: 'aspect--none',
    [MediaBoxAspect.IPHONE6]: 'aspect--phone',
    [MediaBoxAspect.INTRO_BANNER]: 'aspect--intro-banner',
};

export enum MediaBoxType {
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
}

type MediaBoxProps = {
    aspect?: MediaBoxAspect;
    autoplay?: boolean;
    external?: boolean;
    type?: MediaBoxType;
    src?: string;
    video?: string;
    title?: string;
    isFluid?: boolean;
    jsClass?: string;
    jsPlayActionClass?: string;
    hasLoader?: boolean;
    hasShadow?: boolean;
    hasExtraShadow?: boolean;
    hasVideoOpenAnimation?: boolean;
    withinFrame?: boolean;
    volume?: string | number;
    label?: string;
    /**
     * has specific mobile platform behaviour
     */
    mobile?: boolean;
    onExternalPlayClick?: (event: { target: any; data: number }) => void;
    titleElement?: React.ReactNode;
    priority?: boolean;
    a11y?: {
        label: string;
    };
};

const MediaBox: React.FC<React.PropsWithChildren<MediaBoxProps>> = (props) => {
    const {
        aspect = MediaBoxAspect.PORTRAIT,
        autoplay,
        external,
        type = MediaBoxType.IMAGE,
        src,
        video,
        title,
        children,
        isFluid,
        jsClass,
        jsPlayActionClass = 'js-action-play-button',
        hasLoader,
        hasShadow,
        hasExtraShadow,
        hasVideoOpenAnimation,
        volume,
        label,
        mobile,
        withinFrame,
        onExternalPlayClick,
        titleElement,
        priority,
        a11y,
    } = props;
    const classnames = {
        block: classNames(
            {
                'media-box': true,
                'is-fluid': isFluid,
                'has-shadow': hasShadow,
                'has-extra-shadow': hasExtraShadow,
                'has-open-animation': hasVideoOpenAnimation,
                'is-within-frame': withinFrame,
            },
            typeof aspect !== 'undefined' && mapAspectToClasses[aspect],
            typeof jsClass !== 'undefined' && jsClass
        ),
        playButton: classNames({
            'media-box__video-play-button': true,
            'media-box__video-play-button--with-title': titleElement,
            [jsPlayActionClass]: jsPlayActionClass,
        }),
    };
    const mediaBoxPreviewElement = src ? (
        // <div
        //     className="media-box__video-preview js-video-preview-action"
        //     style={{ backgroundImage: src ? `url(${src})` : undefined }}
        //     // alt={title || ''}
        // />
        <Image
            priority={priority}
            className="media-box__video-preview js-video-preview-action"
            src={src}
            alt={title || ''}
            fill={true}
            style={{ objectFit: 'cover' }}
        />
    ) : null;

    const Tag = onExternalPlayClick ? 'button' : 'div';

    const mediaBoxElement = (
        <>
            <figure
                className={classnames.block}
                data-video-id={video}
                data-video-label={label}
                data-video-volume={volume}
                data-video-animated={hasVideoOpenAnimation}
                data-autoplay={autoplay}
                data-external={external}
            >
                {type === MediaBoxType.IMAGE ? (
                    <picture className="media-box__picture">
                        {/* @ts-ignore */}
                        <Tag className="media-box__inner" onClick={onExternalPlayClick}>
                            {src && (
                                <img
                                    className="media-box__picture-image"
                                    src={src}
                                    alt={a11y?.label || ''}
                                />
                            )}

                            {children}

                            {!src && <div className="media-box__placeholder" />}
                        </Tag>
                    </picture>
                ) : null}

                {type === MediaBoxType.VIDEO ? (
                    <Tag
                        className="media-box__inner"
                        // @ts-ignore
                        onClick={onExternalPlayClick}
                        aria-label={a11y?.label}
                    >
                        <span className="media-box__video">
                            {src && mediaBoxPreviewElement}

                            {children}

                            {!src && <div className="media-box__placeholder" />}
                        </span>

                        {hasLoader && (
                            <span className="media-box__video-loader">
                                <Loader
                                    size={LoaderSize.LARGE}
                                    extraClass="is-hidden"
                                    jsClass="js-video-loader"
                                />
                            </span>
                        )}

                        <span className={classnames.playButton}>
                            {titleElement ? (
                                <span className="media-box__video-play-button-title">
                                    {titleElement}
                                </span>
                            ) : null}
                        </span>
                    </Tag>
                ) : null}
            </figure>
            {type === MediaBoxType.VIDEO ? (
                <div
                    className={`media-box-external-video js-video-preview ${
                        mobile ? 'is-mobile' : ''
                    }`}
                />
            ) : null}
            {type === MediaBoxType.VIDEO && hasVideoOpenAnimation ? (
                <div className="media-box-video-close js-video-close">
                    <Icon name={Icon.Name.VideoClose} size="stretch" />
                </div>
            ) : null}
        </>
    );

    if (withinFrame) {
        return <div className="media-box-wrapper">{mediaBoxElement}</div>;
    }

    return mediaBoxElement;
};

export default MediaBox;
